import { tsv } from "@faire/slate/lib/tsv";
import React, { useState } from "react";

import { formatAmount } from "../format";

type MoneyInputProps = {
  value: number;
  rangeMin: number;
  rangeMax: number;
  step: number;
  onCommit: (next: number) => void;
  ariaLabel: string;
  fullWidth?: boolean;
};

/**
 * Slate's TextInput has no currency adornment and no compact chip-sized
 * variant, so the `$`-prefixed field from the designs is built here.
 */
const fieldStyle = tsv({
  base: [
    "border-fs-border-muted flex items-center gap-1 rounded border px-3 py-2",
    "focus-within:border-fs-text-primary",
  ],
  variants: {
    fullWidth: {
      true: "w-full",
      false: "w-30",
    },
  },
});

export const MoneyInput: React.FC<MoneyInputProps> = ({
  value,
  rangeMin,
  rangeMax,
  step,
  onCommit,
  ariaLabel,
  fullWidth = false,
}) => {
  // Null while not editing, so slider drags flow straight through to the field.
  const [draft, setDraft] = useState<string | null>(null);

  const commitDraft = () => {
    if (draft === null) {
      return;
    }
    const parsed = Number.parseInt(draft, 10);
    setDraft(null);
    if (Number.isNaN(parsed)) {
      return;
    }
    const snapped = Math.round(parsed / step) * step;
    onCommit(Math.min(Math.max(snapped, rangeMin), rangeMax));
  };

  return (
    <div className={fieldStyle.cls({ fullWidth })}>
      <span aria-hidden className="type-fs-paragraph text-fs-text-subdued">
        $
      </span>
      <input
        aria-label={ariaLabel}
        inputMode="numeric"
        value={draft ?? formatAmount(value, rangeMax)}
        onFocus={() => setDraft(String(value))}
        onChange={(event) => setDraft(event.target.value.replace(/[^0-9]/g, ""))}
        onBlur={commitDraft}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.currentTarget.blur();
          }
        }}
        className="type-fs-paragraph text-fs-text-primary w-full bg-transparent outline-none"
      />
    </div>
  );
};
